import * as R from 'ramda'
import qs from 'qs'
import { Base64 } from 'js-base64'

import { convertAnswerFilterResponseToRules } from 'libs/convertAdvanceFilter/utils'
import { getExtraSlots } from 'libs/filter-rule-discriminator'
import convertIndexToFilterType, {
  convertFilterTypeToIndex,
  generateDateRanges,
} from 'shared/components/DateRangePicker/utils'
import { convertDateRangeToWorkspaceTimezoneAwareness, getWorkspaceWeekStartDay } from 'libs/date'
import {
  getBaseFilterCustomDashboardOptions,
  getBaseFilterSurveyDashboardOptions,
  getBaseFilterFeedbackOptions,
} from 'hooks/useFilterOptions/constants'

export function generateFilterToken(
  obj,
  customAttributeOptions,
  collaboratorRoleOptions = [],
  weekStartDay = getWorkspaceWeekStartDay()
) {
  const baseFilterOptions = getBaseFilterSurveyDashboardOptions()
  const options = baseFilterOptions.concat(customAttributeOptions, collaboratorRoleOptions)
  return _convertFilterStateToUrlParam(obj, options, weekStartDay)
}

export function generateCustomDashboardFilterToken(
  obj,
  customAttributeOptions,
  collaboratorRoleOptions = [],
  weekStartDay = getWorkspaceWeekStartDay()
) {
  const baseFilterOptions = getBaseFilterCustomDashboardOptions()
  const options = baseFilterOptions.concat(customAttributeOptions, collaboratorRoleOptions)
  return _convertFilterStateToUrlParam(obj, options, weekStartDay)
}

export function generateFeedbackViewFilterToken(
  obj,
  customAttributeOptions,
  collaboratorRoleOptions = [],
  weekStartDay = getWorkspaceWeekStartDay()
) {
  const baseFilterOptions = getBaseFilterFeedbackOptions()
  const options = baseFilterOptions.concat(customAttributeOptions, collaboratorRoleOptions)
  return _convertFilterStateToUrlParam(obj, options, weekStartDay)
}

function _convertFilterStateToUrlParam(obj, options, weekStartDay = getWorkspaceWeekStartDay()) {
  const presetDaterange = generateDateRanges(true, weekStartDay)

  const advancedFilter = R.prop('advanced_filters', obj)
  const dateFilterType = R.prop('date_filter_type', obj)
  const dateRange = convertDateRangeToWorkspaceTimezoneAwareness(
    R.prop('start_date', obj),
    R.prop('end_date', obj)
  )

  const getRules = (filter) => {
    return convertAnswerFilterResponseToRules(filter, options)
  }

  if (!R.isNil(dateRange) && !R.isNil(dateFilterType)) {
    const filterQuery =
      R.isEmpty(advancedFilter) || R.has('global_operator', advancedFilter)
        ? {
            globalOperator: advancedFilter?.['global_operator'] || 'and',
            groups: getRules(advancedFilter),
          }
        : {
            condition: 'and',
            rules: getRules(advancedFilter),
          }

    let dateRangeFilterQuery = {}
    if (dateFilterType != 0) {
      const selectedPresetIndex = convertFilterTypeToIndex(dateFilterType)
      const selectDateRange = R.path([selectedPresetIndex, 'dateRange'], presetDaterange)
      dateRangeFilterQuery = {
        open: false,
        dateRange: selectDateRange,
        selectedPresetIndex: selectedPresetIndex,
      }
    } else {
      dateRangeFilterQuery = {
        open: false,
        dateRange: dateRange,
        selectedPresetIndex: null,
      }
    }

    const state = { advancedFilterQuery: filterQuery, dateRangeFilterQuery: dateRangeFilterQuery }
    const search = encodeFilterToUrlParam(state)
    return search
  } else {
    return ''
  }
}

export function encodeFilterToUrlParam(filters) {
  const hasAdvancedRulesQuery = !R.isEmpty(R.path(['advancedFilterQuery', 'rules'], filters))
  const hasAdvancedGroupsQuery = !R.isEmpty(R.path(['advancedFilterQuery', 'groups'], filters))
  const hasDateRangeQuery = !R.isEmpty(R.path(['dateRangeFilterQuery'], filters))

  if (hasAdvancedRulesQuery || hasAdvancedGroupsQuery || hasDateRangeQuery) {
    const searchParameters = JSON.stringify(filters)
    const queryString = Base64.encodeURI(searchParameters)

    return `?filter=${queryString}`
  } else {
    return ''
  }
}

export function decodeFilterFromUrlParam(searchParameters) {
  const hasQuery = !R.isEmpty(searchParameters)
  if (!hasQuery) {
    return
  }
  const encodedFilter = qs.parse(searchParameters, { ignoreQueryPrefix: true }).filter

  return JSON.parse(Base64.decode(encodedFilter))
}

export function getAdvanceFilterSearch(search) {
  const state = decodeFilterFromUrlParam(search)
  const filter = R.prop('advancedFilterQuery', state)
  const globalOperator = R.prop('globalOperator', filter)
  if (filter) {
    if (globalOperator) {
      return {
        global_operator: globalOperator,
        groups: filter.groups?.map((group) => {
          return {
            operator: group.operator,
            filters: group.rules?.map((rule) => {
              return {
                key: rule.field,
                filtered_values: rule.filters?.map((filter) => filter.value),
                ...getExtraSlots(rule.extra),
                comparison: rule.comparison,
              }
            }),
          }
        }),
      }
    } else {
      return {
        [filter.condition]: filter.rules?.map((rule) => {
          return {
            key: rule.field,
            filtered_values: rule.filters?.map((filter) => filter.value),
            ...getExtraSlots(rule.extra),
            comparison: rule.comparison,
          }
        }),
      }
    }
  } else {
    return {}
  }
}

// use to request api
export function getDateRangeFilterSearch(search) {
  const state = decodeFilterFromUrlParam(search)

  const filteredRange = R.path(['dateRangeFilterQuery', 'dateRange'], state)
  const filterDatePresetOption = R.path(['dateRangeFilterQuery', 'selectedPresetIndex'], state)
  const presetIndexOption = R.isNil(filterDatePresetOption)
    ? 0
    : convertIndexToFilterType(filterDatePresetOption)

  return filteredRange ? [...filteredRange, presetIndexOption] : ['', '', presetIndexOption]
}

export function getDecodedInitialFilter(weekStartDay = getWorkspaceWeekStartDay()) {
  const presetDaterange = generateDateRanges(true, weekStartDay)

  const filterQuery = {
    globalOperator: 'and',
    groups: [],
  }

  const selectDateRange = R.path([4, 'dateRange'], presetDaterange)
  const dateRangeFilterQuery = {
    open: false,
    dateRange: selectDateRange,
    selectedPresetIndex: 5,
  }

  const state = { advancedFilterQuery: filterQuery, dateRangeFilterQuery: dateRangeFilterQuery }

  return state
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/shared/components/FilterGroupV2/utils.js